// React imports
import { FC, ReactNode, useContext } from "react";

// Library imports
import { Link } from "react-router-dom";

// CSS imports
import "./FreeTrialButton.css";
import { AuthContext, AuthContextType } from "../context/AuthProvider";
import { trackButtonClick, GAEventName } from "./Analytics/googleAnalytics";

interface FreeTrialButtonProps {
	/**
	 * The path to navigate to when the link is clicked.
	 * @defaultValue The value of the environment variable
	 * `REACT_APP_STRIPE_FREE_TRIAL_URL` or `"/login"` if it is not defined.
	 */
	path?: string;
	/**
	 * The classes to apply to the anchor element.
	 * @defaultValue `"free-trial-button"`
	 */
	className?: string;
	children?: ReactNode;
	/**
	 * The Google Analytics event name to track when the button is clicked.
	 * @defaultValue 'FreeTrial'
	 */
	gaEventName?: GAEventName;
	/**
	 * The location on the page where the button appears (for analytics).
	 * @defaultValue 'unknown'
	 */
	gaLocation?: string;
}

/**
 * A free trial link intended to appear as a clickable button.
 *
 *  - If the user is already logged in, it points to `/dashboard`
 *  - Otherwise, if the environment variable `REACT_APP_STRIPE_FREE_TRIAL_URL` is
 * defined, it points to that URL.
 *  - If that variable is not defined, it points to `/login`
 *
 * Its appearance can be customized with the `className` prop.
 */
const FreeTrialButton: FC<FreeTrialButtonProps> = ({
	path: pathname = process.env.REACT_APP_STRIPE_FREE_TRIAL_URL ?? "/login",
	className = "free-trial-button",
	children,
	gaEventName = "FreeTrial",
	gaLocation = "unknown"
}) => {
	const { user } = useContext(AuthContext) as AuthContextType;

	const handleClick = () => {
		const buttonText = typeof children === "string" ? children : "Free Trial";
		trackButtonClick(gaEventName, buttonText, gaLocation);
	};

	return (
		<Link
			className={className}
			to={user ? "/dashboard" : pathname}
			onClick={handleClick}
		>
			{children}
		</Link>
	);
};

export default FreeTrialButton;
